/**
 * The wheelhouse instruments, as DOM.
 *
 * HTML over the canvas, not in-scene UI: it costs nothing per frame, scales
 * with the window for free, and screen readers can see it. Everything updates
 * by mutating textContent/styles — no per-frame DOM construction.
 */

import { bindHoldControls } from './HoldControls.ts'
import { BREAKING_TENSION } from '../entities/Hawser.ts'
import type { Contract } from './Contracts.ts'
import type { SeaCondition } from './Weather.ts'

const COMPASS_POINTS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']

export class Hud {
  private readonly throttleFill: HTMLElement
  private readonly rudderMarker: HTMLElement
  private readonly speedValue: HTMLElement
  private readonly headingValue: HTMLElement
  private readonly tensionFill: HTMLElement
  private readonly tensionWrap: HTMLElement
  private readonly contractText: HTMLElement
  private readonly contractClock: HTMLElement
  private readonly earningsValue: HTMLElement
  private readonly toast: HTMLElement
  private readonly hint: HTMLElement
  private toastTimer = 0
  private baseHint = ''
  private rampState: boolean | null = null

  private readonly root: HTMLElement

  constructor(root: HTMLElement) {
    this.root = root
    root.innerHTML = `
      <div class="hud-top">
        <button class="hud-btn hud-menu" title="Back to the menu" aria-label="Back to menu"><span class="hud-btn-icon icon-house" aria-hidden="true"></span></button>
        <div class="hud-earnings">$<span>0</span></div>
        <div class="hud-contract">
          <div class="hud-contract-text">Awaiting orders…</div>
          <div class="hud-contract-clock"></div>
        </div>
      </div>
      <div class="hud-toast" role="status" hidden></div>
      <div class="hud-driving">
        <div class="hud-controls hud-controls-left" role="group" aria-label="Throttle">
          <span class="hud-control-label">Speed</span>
          <div class="hud-throttle-layout">
            <div class="hud-throttle-col">
              <button class="hud-btn" data-key="KeyW" title="Throttle up" aria-label="Throttle up"><span class="hud-btn-icon icon-chevron-up" aria-hidden="true"></span></button>
              <button class="hud-btn" data-key="KeyS" title="Throttle down" aria-label="Throttle down"><span class="hud-btn-icon icon-chevron-down" aria-hidden="true"></span></button>
            </div>
            <div class="hud-throttle-track" aria-hidden="true"><div class="hud-throttle-fill"></div></div>
          </div>
        </div>
        <div class="hud-bottom">
          <div class="hud-readouts">
            <div class="hud-speed"><span>0.0</span><small>kn</small></div>
            <div class="hud-heading"><span>N 000</span></div>
          </div>
          <div class="hud-gauge hud-tension" hidden>
            <div class="hud-gauge-label">TOW TENSION</div>
            <div class="hud-tension-track"><div class="hud-tension-fill"></div></div>
          </div>
        </div>
        <div class="hud-controls hud-controls-right" role="group" aria-label="Steering">
          <span class="hud-control-label">Steer</span>
          <div class="hud-rudder-track" aria-hidden="true"><div class="hud-rudder-marker"></div></div>
          <div class="hud-helm-row">
            <button class="hud-btn" data-key="KeyA" title="Helm to port" aria-label="Steer left"><span class="hud-btn-icon icon-chevron-left" aria-hidden="true"></span></button>
            <button class="hud-btn" data-key="KeyD" title="Helm to starboard" aria-label="Steer right"><span class="hud-btn-icon icon-chevron-right" aria-hidden="true"></span></button>
          </div>
        </div>
      </div>
      <div class="hud-controls hud-controls-actions">
        <button class="hud-btn hud-btn-wide hud-ramp" data-key="KeyE" title="Lower the ramp" aria-label="Lower the ramp" hidden><span class="hud-btn-icon icon-arrow-down-to-line" aria-hidden="true"></span><span class="hud-btn-label hud-ramp-label">Open ramp</span></button>
        <button class="hud-btn hud-btn-wide hud-hook" data-key="Space" title="Hook the tow" aria-label="Hook the tow" hidden><span class="hud-btn-icon icon-anchor" aria-hidden="true"></span><span class="hud-btn-label hud-hook-label">Hook</span></button>
      </div>
      <div class="hud-controls hud-controls-view">
        <button class="hud-btn hud-mute" title="Mute sound" aria-label="Mute sound"><span class="hud-btn-icon icon-volume-2" aria-hidden="true"></span></button>
        <button class="hud-btn hud-btn-wide hud-weather" data-key="KeyV" title="Change weather" aria-label="Change weather (Calm)"><span class="hud-btn-icon icon-sun" aria-hidden="true"></span><span class="hud-btn-label hud-weather-label">Calm</span></button>
        <button class="hud-btn hud-btn-wide hud-view" data-key="KeyC" title="Change view" aria-label="Change view (Chase)"><span class="hud-btn-icon icon-camera" aria-hidden="true"></span><span class="hud-btn-label hud-view-label">Chase</span></button>
      </div>
      <div class="hud-hint">W/S throttle · A/D helm · R midships · Space hook/release · C camera · V weather</div>
    `

    const get = (selector: string): HTMLElement => root.querySelector(selector)!
    this.throttleFill = get('.hud-throttle-fill')
    this.rudderMarker = get('.hud-rudder-marker')
    this.speedValue = get('.hud-speed span')
    this.headingValue = get('.hud-heading span')
    this.tensionFill = get('.hud-tension-fill')
    this.tensionWrap = get('.hud-tension')
    this.contractText = get('.hud-contract-text')
    this.contractClock = get('.hud-contract-clock')
    this.earningsValue = get('.hud-earnings span')
    this.toast = get('.hud-toast')
    this.hint = get('.hud-hint')
  }

  setMenuOpen(open: boolean): void {
    this.root.hidden = open
  }

  setView(name: string): void {
    const button = this.root.querySelector<HTMLButtonElement>('.hud-view')!
    button.querySelector('.hud-view-label')!.textContent = name
    button.setAttribute('aria-label', `Change view (${name})`)
    button.title = `Change view (${name})`
  }

  setWeather(condition: SeaCondition): void {
    const [label, icon] = {
      calm: ['Calm', 'icon-sun'], rough: ['Waves', 'icon-waves'],
      storm: ['Storm', 'icon-cloud-rain'], 'perfect-storm': ['Big storm', 'icon-cloud-lightning'],
    }[condition]
    const button = this.root.querySelector<HTMLButtonElement>('.hud-weather')!
    button.querySelector('.hud-weather-label')!.textContent = label
    button.querySelector('.hud-btn-icon')!.className = `hud-btn-icon ${icon}`
    button.setAttribute('aria-label', `Change weather (${label})`)
    button.title = `Change weather (${label})`
  }

  /** Keep only driving controls in open water; restore objectives when switching jobs. */
  setFreeDrive(enabled: boolean): void {
    this.root.querySelector<HTMLElement>('.hud-earnings')!.hidden = enabled
    this.root.querySelector<HTMLElement>('.hud-contract')!.hidden = enabled
    this.toast.hidden = true
    this.toastTimer = 0
    this.setHint(enabled
      ? 'W/S throttle · A/D helm · R midships · C camera · V weather'
      : 'W/S throttle · A/D helm · R midships · Space hook/release · C camera · V weather')
    this.setHookState(false, false)
  }

  /** Ice keeps its progress banner, with no money or weather controls. */
  setIceMode(enabled: boolean): void {
    this.root.querySelector<HTMLElement>('[data-key="KeyV"]')!.hidden = enabled
    if (enabled) {
      this.root.querySelector<HTMLElement>('.hud-earnings')!.hidden = true
      this.root.querySelector<HTMLElement>('.hud-contract')!.hidden = false
      this.setHint('W/S throttle · A/D helm · R midships · C camera · Power ahead to break ice')
    }
  }

  setThrottle(value: number): void {
    // Fill grows up from the middle for ahead, down for astern.
    this.throttleFill.style.height = `${Math.abs(value) * 50}%`
    this.throttleFill.style.top = value >= 0 ? `${50 - Math.abs(value) * 50}%` : '50%'
    this.throttleFill.style.background = value >= 0 ? 'var(--ok)' : 'var(--warn)'
  }

  setRudder(value: number): void {
    this.rudderMarker.style.left = `${50 + value * 46}%`
  }

  setSpeedHeading(metresPerSecond: number, yaw: number): void {
    this.speedValue.textContent = (metresPerSecond * 1.9438).toFixed(1)
    // Yaw is math-positive about +Y; compass runs the other way.
    let degrees = Math.round((((-yaw * 180) / Math.PI) % 360 + 360) % 360)
    const point = COMPASS_POINTS[Math.round(degrees / 45) % 8]
    this.headingValue.textContent = `${point} ${String(degrees).padStart(3, '0')}`
  }

  setTension(newtons: number, connected: boolean): void {
    this.tensionWrap.hidden = !connected
    if (!connected) return
    const fraction = Math.min(newtons / BREAKING_TENSION, 1)
    this.tensionFill.style.width = `${fraction * 100}%`
    this.tensionFill.style.background =
      fraction > 0.75 ? 'var(--danger)' : fraction > 0.45 ? 'var(--warn)' : 'var(--ok)'
  }

  setContract(contract: Contract | null, earnings: number, distanceMetres = 0): void {
    this.earningsValue.textContent = earnings.toLocaleString()
    if (!contract) {
      this.contractText.textContent = 'Awaiting orders…'
      this.contractClock.textContent = ''
      return
    }
    const away =
      distanceMetres >= 30
        ? distanceMetres >= 950
          ? ` · ${(distanceMetres / 1000).toFixed(1)} km away`
          : ` · ${Math.round(distanceMetres / 10) * 10} m away`
        : ''
    this.contractText.textContent = contract.description + away
    const remaining = Math.round(contract.timeAllowed - contract.timeUsed)
    const overdue = remaining < 0
    const t = Math.abs(remaining)
    this.contractClock.textContent = `${overdue ? '-' : ''}${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`
    this.contractClock.style.color = overdue ? 'var(--danger)' : ''
  }

  /**
   * Generic objective banner, for modes without a timed contract (Beach
   * Salvage). `subline` is the small grey line under the main text — a count or
   * a hint rather than a clock.
   */
  setMission(text: string, subline: string, earnings: number): void {
    this.earningsValue.textContent = earnings.toLocaleString()
    this.contractText.textContent = text
    this.contractClock.textContent = subline
    this.contractClock.style.color = ''
  }

  /**
   * Wire the on-screen buttons to the input layer. Held buttons behave exactly
   * like held keys — same rates, same one-shot semantics for HOOK and VIEW —
   * so mouse, touch and keyboard can be mixed freely mid-manoeuvre.
   */
  bindControls(input: { virtualPress(code: string): void; virtualRelease(code: string): void }): () => void {
    return bindHoldControls(this.root.querySelectorAll<HTMLButtonElement>('.hud-btn'), input)
  }

  /** Wire the MENU button to hand control back to the title screen. */
  bindMenu(handler: () => void): void {
    this.root.querySelector<HTMLButtonElement>('.hud-menu')!.addEventListener('click', (event) => {
      ;(event.currentTarget as HTMLElement).blur()
      handler()
    })
  }

  /** Wire the mute toggle; `handler` returns the new muted state to redraw the icon. */
  bindMute(handler: () => boolean): void {
    const button = this.root.querySelector<HTMLButtonElement>('.hud-mute')!
    const icon = button.querySelector<HTMLElement>('.hud-btn-icon')!
    button.addEventListener('click', (event) => {
      ;(event.currentTarget as HTMLElement).blur()
      const muted = handler()
      icon.className = muted ? 'hud-btn-icon icon-volume-x' : 'hud-btn-icon icon-volume-2'
      button.title = muted ? 'Unmute sound' : 'Mute sound'
      button.setAttribute('aria-label', button.title)
      button.classList.toggle('hud-mute-on', muted)
    })
  }

  /**
   * The hook button earns its screen space: hidden until a line could actually
   * be passed, and honest about which way the toggle will go.
   */
  setHookState(withinRange: boolean, connected: boolean): void {
    const button = this.root.querySelector<HTMLButtonElement>('.hud-hook')!
    button.hidden = !connected && !withinRange
    const label = button.querySelector('.hud-hook-label')!
    const icon = button.querySelector('.hud-btn-icon')!
    const next = connected ? 'Detach' : 'Hook'
    if (label.textContent !== next) {
      label.textContent = next
      icon.className = connected ? 'hud-btn-icon icon-unlink' : 'hud-btn-icon icon-anchor'
      button.title = connected ? 'Detach the tow' : 'Hook the tow'
      button.setAttribute('aria-label', button.title)
    }
  }

  /** Hide the money chip for jobs that aren't paid by the tow. */
  setEarningsHidden(hidden: boolean): void {
    this.root.querySelector<HTMLElement>('.hud-earnings')!.hidden = hidden
  }

  /**
   * The landing ramp button: hidden for boats without one (`null`), and
   * labelled with what pressing it will do.
   */
  setRamp(open: boolean | null): void {
    if (open === this.rampState) return
    const hadRamp = this.rampState !== null
    this.rampState = open
    const button = this.root.querySelector<HTMLButtonElement>('.hud-ramp')!
    button.hidden = open === null
    if (hadRamp !== (open !== null)) this.setHint(this.baseHint)
    if (open === null) return
    button.querySelector('.hud-ramp-label')!.textContent = open ? 'Close ramp' : 'Open ramp'
    button.querySelector('.hud-btn-icon')!.className = `hud-btn-icon ${open ? 'icon-arrow-up-from-line' : 'icon-arrow-down-to-line'}`
    button.title = open ? 'Raise the ramp' : 'Lower the ramp'
    button.setAttribute('aria-label', button.title)
  }

  /** Transient message, centre screen. */
  announce(message: string, seconds = 4): void {
    this.toast.textContent = message
    this.toast.hidden = false
    this.toastTimer = seconds
  }

  setHint(text: string): void {
    this.baseHint = text
    this.hint.textContent = this.rampState === null ? text : text.replace(' · C camera', ' · E ramp · C camera')
  }

  update(dt: number): void {
    if (this.toastTimer > 0) {
      this.toastTimer -= dt
      if (this.toastTimer <= 0) this.toast.hidden = true
    }
  }
}
